/**
 * PopulationPreviewDialog.jsx
 *
 * Inspector dialog for a population. Surfaces:
 *   • Top-line metadata (rows, columns, created, pretrained?)
 *   • Sample rows (first 10)
 *   • Per-column stats: unique, missing %, top-value mini bars
 *
 * Goal: turn populations from static rows into something users can inspect,
 * builds enterprise-grade trust in the data.
 */
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Badge } from './ui/badge';
import { Loader2, Calendar, Database, FlaskConical, AlertCircle, Hash, Type } from 'lucide-react';
import api from '../lib/api';
import { formatDate } from '../lib/formatters';

function MiniBar({ pct }) {
  return (
    <div className="h-1.5 w-full bg-slate-100 dark:bg-muted rounded-full overflow-hidden">
      <div
        className="h-full bg-blue-500/60 dark:bg-blue-500/70"
        style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
      />
    </div>
  );
}

function ColumnRow({ col }) {
  const Icon = col.is_categorical ? Type : Hash;
  return (
    <div className="border border-slate-100 dark:border-border rounded p-3 space-y-1.5" data-testid={`col-row-${col.name}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          <span className="font-medium text-sm text-slate-800 dark:text-slate-100 truncate">{col.name}</span>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <Badge variant="outline" className="text-[10px] bg-slate-50 dark:bg-muted text-slate-600 dark:text-slate-300">{col.dtype}</Badge>
          {col.missing_pct > 0 && (
            <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
              {col.missing_pct}% missing
            </Badge>
          )}
        </div>
      </div>
      <div className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-3">
        <span>{col.unique} unique</span>
        {col.scanned > 0 && <span>· scanned {col.scanned.toLocaleString()}</span>}
      </div>
      {col.is_categorical && col.distribution.length > 0 && (
        <div className="space-y-1 pt-1">
          {col.distribution.slice(0, 5).map((d) => (
            <div key={d.value} className="grid grid-cols-[1fr_50px_40px] items-center gap-2 text-[11px]">
              <span className="text-slate-600 dark:text-slate-300 truncate">{d.value}</span>
              <MiniBar pct={d.pct} />
              <span className="text-slate-500 dark:text-slate-400 font-mono tabular-nums text-right">{d.pct}%</span>
            </div>
          ))}
          {col.distribution.length > 5 && (
            <p className="text-[10px] text-slate-400">+{col.distribution.length - 5} more values</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function PopulationPreviewDialog({ open, onOpenChange, populationId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !populationId) return;
    setLoading(true);
    setError(null);
    setData(null);
    api.get(`/populations/${populationId}/preview`, { _silent: true })
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.detail || 'Failed to load preview'))
      .finally(() => setLoading(false));
  }, [open, populationId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="population-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Database className="w-4 h-4 text-slate-500" />
            {data?.name || 'Population preview'}
          </DialogTitle>
          {data?.description && (
            <DialogDescription className="text-xs text-slate-500">{data.description}</DialogDescription>
          )}
        </DialogHeader>

        {loading && (
          <div className="h-48 flex items-center justify-center text-slate-500 text-sm" data-testid="population-preview-loading">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading inspector…
          </div>
        )}

        {error && (
          <div className="h-32 flex items-center justify-center gap-2 text-red-600 text-sm" data-testid="population-preview-error">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}

        {data && (
          <div className="space-y-4">
            {/* Top-line metadata */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="population-preview-meta">
              <div className="rounded border border-slate-200 dark:border-border p-2">
                <p className="text-[10px] uppercase tracking-wide text-slate-400">Rows</p>
                <p className="text-lg font-semibold tabular-nums">{(data.total_individuals || 0).toLocaleString()}</p>
              </div>
              <div className="rounded border border-slate-200 dark:border-border p-2">
                <p className="text-[10px] uppercase tracking-wide text-slate-400">Columns</p>
                <p className="text-lg font-semibold tabular-nums">{data.columns.length}</p>
              </div>
              <div className="rounded border border-slate-200 dark:border-border p-2">
                <p className="text-[10px] uppercase tracking-wide text-slate-400 flex items-center gap-1"><Calendar className="w-3 h-3" />Uploaded</p>
                <p className="text-sm font-medium">{formatDate(data.created_at) || '—'}</p>
              </div>
              <div className="rounded border border-slate-200 dark:border-border p-2">
                <p className="text-[10px] uppercase tracking-wide text-slate-400 flex items-center gap-1"><FlaskConical className="w-3 h-3" />Pretrain</p>
                <p className="text-sm font-medium">{data.pretrained ? `${data.pretrain_count} entries` : '—'}</p>
              </div>
            </div>

            {/* Sample rows */}
            {data.sample_rows.length > 0 && (
              <div className="space-y-1.5" data-testid="population-preview-sample">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Sample rows (first {data.sample_rows.length})</p>
                <div className="overflow-x-auto border border-slate-200 dark:border-border rounded">
                  <table className="text-xs w-full">
                    <thead className="bg-slate-50 dark:bg-muted">
                      <tr>
                        {data.sample_columns.slice(0, 8).map(c => (
                          <th key={c} className="text-left p-2 font-medium text-slate-600 dark:text-slate-300 whitespace-nowrap">{c}</th>
                        ))}
                        {data.sample_columns.length > 8 && (
                          <th className="text-left p-2 font-medium text-slate-400">+{data.sample_columns.length - 8} more</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {data.sample_rows.map(r => (
                        <tr key={r.id} className="border-t border-slate-100 dark:border-border/50 hover:bg-slate-50/40 dark:hover:bg-white/[0.02]">
                          {data.sample_columns.slice(0, 8).map(c => (
                            <td key={c} className="p-2 text-slate-700 dark:text-slate-200 whitespace-nowrap max-w-[140px] truncate">
                              {r[c] !== undefined && r[c] !== null && r[c] !== '' ? String(r[c]) : <span className="text-slate-300">—</span>}
                            </td>
                          ))}
                          {data.sample_columns.length > 8 && <td className="p-2 text-slate-300">…</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Columns */}
            {data.columns.length > 0 && (
              <div className="space-y-1.5" data-testid="population-preview-columns">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Columns &amp; distributions</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {data.columns.map(col => <ColumnRow key={col.name} col={col} />)}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
